import axios, { type AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';
import type {
  ApiError,
  ApiPaginatedSuccess,
  ApiResponse,
  PaginationMeta,
  RefreshResponseData,
} from '@cosvision/shared';
import { CLIENT_PLATFORM_HEADER, clientPlatform, isNativePlatform } from './platform.js';
import { getStoredRefreshToken } from './native-refresh-store.js';

// Web builds use the same-origin relative path (behaviour unchanged); the
// mobile build injects the absolute VPS URL via .env.mobile because the
// Capacitor WebView origin (https://localhost) is not where the API lives.
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

interface AuthTokenAccessor {
  getAccessToken: () => string | null;
  setAccessToken: (token: string | null) => void;
  onUnauthenticated: () => void;
}

let tokenAccessor: AuthTokenAccessor | null = null;

export function registerTokenAccessor(accessor: AuthTokenAccessor): void {
  tokenAccessor = accessor;
}

/**
 * Bare axios with cookies enabled (so the web refresh cookie travels). On
 * native, every request additionally carries X-Client-Platform so the auth
 * endpoints use body-transport refresh instead of cookies.
 */
function makeClient(): AxiosInstance {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const platform = clientPlatform();
  if (platform) headers[CLIENT_PLATFORM_HEADER] = platform;
  return axios.create({
    baseURL: API_BASE,
    withCredentials: true,
    headers,
    timeout: 30_000,
  });
}

export const api: AxiosInstance = makeClient();

// Refresh deduplication — if many requests 401 simultaneously, only one refresh fires.
let refreshInFlight: Promise<string | null> | null = null;

async function attemptRefresh(): Promise<string | null> {
  if (!tokenAccessor) return null;
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      // Native: the refresh token rides the body (from the Keystore). No
      // stored token means there is no session to refresh — fail fast.
      let body: { refreshToken: string } | undefined;
      if (isNativePlatform()) {
        const stored = await getStoredRefreshToken();
        if (!stored) return null;
        body = { refreshToken: stored };
      }
      // Use a bare axios instance to avoid triggering our own interceptor.
      const bare = makeClient();
      const res = await bare.post<ApiResponse<RefreshResponseData>>('/auth/refresh', body);
      if (res.data.success) {
        tokenAccessor?.setAccessToken(res.data.data.accessToken);
        return res.data.data.accessToken;
      }
      return null;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

// Request interceptor — attach Bearer token if we have one.
api.interceptors.request.use((config) => {
  const token = tokenAccessor?.getAccessToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

// Response interceptor — on 401, try a single refresh + retry, else logout.
api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
    if (!original || error.response?.status !== 401 || original._retried) {
      throw error;
    }
    // Don't try to refresh the refresh endpoint itself.
    if (original.url?.includes('/auth/refresh') || original.url?.includes('/auth/login')) {
      throw error;
    }
    original._retried = true;
    const newToken = await attemptRefresh();
    if (!newToken) {
      tokenAccessor?.onUnauthenticated();
      throw error;
    }
    original.headers = { ...(original.headers ?? {}), Authorization: `Bearer ${newToken}` };
    return api.request(original);
  },
);

/**
 * Unwrap an `ApiResponse<T>` payload. Throws an `ApiClientError` on failure
 * envelopes so callers can use try/catch instead of branching on `success`.
 */
export class ApiClientError extends Error {
  public readonly code?: string;
  public readonly errors?: Record<string, string[]>;
  public readonly status?: number;
  public readonly details?: Record<string, unknown>;
  constructor(
    message: string,
    opts: {
      code?: string;
      errors?: Record<string, string[]>;
      status?: number;
      details?: Record<string, unknown>;
    } = {},
  ) {
    super(message);
    this.name = 'ApiClientError';
    if (opts.code !== undefined) this.code = opts.code;
    if (opts.errors !== undefined) this.errors = opts.errors;
    if (opts.status !== undefined) this.status = opts.status;
    if (opts.details !== undefined) this.details = opts.details;
  }
}

function mapAxiosError(err: unknown): never {
  if (err instanceof ApiClientError) throw err;
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as
      | {
          message?: string;
          code?: string;
          errors?: Record<string, string[]>;
          details?: Record<string, unknown>;
        }
      | undefined;
    throw new ApiClientError(body?.message ?? err.message, {
      code: body?.code,
      errors: body?.errors,
      details: body?.details,
      status: err.response?.status,
    });
  }
  throw err;
}

export async function request<T>(
  method: 'get' | 'post' | 'put' | 'patch' | 'delete',
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  try {
    const res = await api.request<ApiResponse<T>>({ method, url, data, ...config });
    const body = res.data;
    if (body.success) return body.data;
    throw new ApiClientError(body.message, {
      code: body.code,
      errors: body.errors,
      details: body.details,
      status: res.status,
    });
  } catch (err) {
    mapAxiosError(err);
  }
}

/**
 * Variant of `request` that preserves pagination meta. The backend returns
 * `ApiPaginatedSuccess<T>` with both `data: T[]` and `meta` — `request()`
 * would drop the meta because it unwraps `data`.
 */
export async function requestPaginated<T>(
  method: 'get' | 'post',
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<{ data: T[]; meta: PaginationMeta }> {
  try {
    const res = await api.request<ApiPaginatedSuccess<T> | ApiError>({
      method,
      url,
      data,
      ...config,
    });
    const body = res.data;
    if (body.success) return { data: body.data, meta: body.meta };
    throw new ApiClientError(body.message, {
      code: body.code,
      errors: body.errors,
      details: body.details,
      status: res.status,
    });
  } catch (err) {
    mapAxiosError(err);
  }
}

/**
 * Variant of `request` for endpoints that return 204 No Content (DELETE,
 * mostly). Does not try to unwrap an envelope.
 */
export async function requestNoContent(
  method: 'delete' | 'post' | 'patch' | 'put',
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<void> {
  try {
    await api.request({ method, url, data, ...config });
  } catch (err) {
    mapAxiosError(err);
  }
}
